import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, SendIcon, CheckCircle2Icon, MessageCircleIcon } from 'lucide-react';
import { SectionHeading } from './shared/SectionHeading';
const PHONE = '+387 61 000 000';
const PHONE_HREF = 'tel:+38761000000';
const WHATSAPP_HREF = 'https://wa.me/38761000000';
interface FormState {
  ime: string;
  email: string;
  telefon: string;
  poruka: string;
}
interface FormErrors {
  ime?: string;
  email?: string;
  telefon?: string;
  poruka?: string;
}
const initial: FormState = {
  ime: '',
  email: '',
  telefon: '',
  poruka: ''
};
const infoCards = [{
  icon: PhoneIcon,
  label: 'Telefon',
  value: PHONE,
  href: PHONE_HREF
}, {
  icon: MailIcon,
  label: 'Email',
  value: 'info@goldencare.ba',
  href: 'mailto:info@goldencare.ba'
}, {
  icon: MapPinIcon,
  label: 'Lokacija',
  value: 'Tuzla, Bosna i Hercegovina'
}, {
  icon: ClockIcon,
  label: 'Radno vrijeme',
  value: 'Dostupni 24/7'
}];
export function Contact() {
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.ime.trim()) e.ime = 'Molimo unesite vaše ime.';
    if (!form.email.trim()) e.email = 'Molimo unesite email adresu.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Unesite ispravnu email adresu.';
    if (!form.telefon.trim()) e.telefon = 'Molimo unesite broj telefona.';
    if (!form.poruka.trim()) e.poruka = 'Molimo napišite vašu poruku.';else if (form.poruka.trim().length < 10) e.poruka = 'Poruka treba imati najmanje 10 znakova.';
    return e;
  };
  const handleChange = (field: keyof FormState, value: string) => {
    setForm(f => ({
      ...f,
      [field]: value
    }));
    if (errors[field]) setErrors(er => ({
      ...er,
      [field]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSent(true);
      setForm(initial);
      setTimeout(() => setSent(false), 6000);
    }, 1200);
  };
  const fieldClass = (has?: string) => `w-full rounded-xl border bg-white px-4 py-3.5 text-ink outline-none transition-colors placeholder:text-muted/60 focus:border-gold focus:ring-2 focus:ring-gold/20 ${has ? 'border-red-400' : 'border-black/10'}`;
  return <section id="kontakt" className="relative w-full bg-white py-20 lg:py-28" data-id="element-44">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-45">
        <SectionHeading eyebrow="Kontakt" title="Razgovarajmo o vašim potrebama" subtitle="Javite nam se — pružit ćemo vam savjet i pronaći najbolje rješenje za vašu porodicu." data-id="element-46" />

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2" data-id="element-47">
          {/* Left: info + map + quick actions */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex flex-col gap-6" data-id="element-48">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-49">
              {infoCards.map(c => {
              const inner = <>
                    <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gold-gradient text-white shadow-gold" data-id="element-50">
                      <c.icon className="h-5 w-5" data-id="element-51" />
                    </span>
                    <span className="block text-sm font-semibold uppercase tracking-wide text-muted" data-id="element-52">
                      {c.label}
                    </span>
                    <span className="mt-1 block font-medium text-ink" data-id="element-53">
                      {c.value}
                    </span>
                  </>;
              return c.href ? <a key={c.label} href={c.href} className="rounded-2xl border border-cream bg-cream/50 p-5 transition-colors hover:border-gold/30 hover:bg-white" data-id="element-54">
                    {inner}
                  </a> : <div key={c.label} className="rounded-2xl border border-cream bg-cream/50 p-5" data-id="element-55">
                    {inner}
                  </div>;
            })}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-56">
              <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-95" data-id="element-57">
                <MessageCircleIcon className="h-5 w-5" data-id="element-58" />
                WhatsApp
              </a>
              <a href={PHONE_HREF} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-gold/40 bg-white px-6 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-cream" data-id="element-59">
                <PhoneIcon className="h-5 w-5 text-gold" data-id="element-60" />
                Pozovite nas
              </a>
            </div>

            <div className="overflow-hidden rounded-2xl shadow-glass ring-1 ring-black/5" data-id="element-61">
              <iframe title="Golden Care lokacija — Tuzla" src="https://www.openstreetmap.org/export/embed.html?bbox=18.62%2C44.51%2C18.72%2C44.57&layer=mapnik&marker=44.5382%2C18.6734" className="h-64 w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-62" />
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} onSubmit={handleSubmit} noValidate className="rounded-3xl bg-cream p-7 shadow-glass ring-1 ring-gold/10 sm:p-9" data-id="element-63">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-64">
              <div className="sm:col-span-2" data-id="element-65">
                <label htmlFor="ime" className="mb-2 block text-sm font-medium text-ink" data-id="element-66">
                  Ime i prezime
                </label>
                <input id="ime" type="text" value={form.ime} onChange={e => handleChange('ime', e.target.value)} placeholder="Vaše ime i prezime" className={fieldClass(errors.ime)} aria-invalid={!!errors.ime} data-id="element-67" />
                {errors.ime && <p className="mt-1.5 text-sm text-red-500" data-id="element-68">{errors.ime}</p>}
              </div>

              <div data-id="element-69">
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-ink" data-id="element-70">
                  Email
                </label>
                <input id="email" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} placeholder="vas@email.ba" className={fieldClass(errors.email)} aria-invalid={!!errors.email} data-id="element-71" />
                {errors.email && <p className="mt-1.5 text-sm text-red-500" data-id="element-72">{errors.email}</p>}
              </div>

              <div data-id="element-73">
                <label htmlFor="telefon" className="mb-2 block text-sm font-medium text-ink" data-id="element-74">
                  Telefon
                </label>
                <input id="telefon" type="tel" value={form.telefon} onChange={e => handleChange('telefon', e.target.value)} placeholder="+387 ..." className={fieldClass(errors.telefon)} aria-invalid={!!errors.telefon} data-id="element-75" />
                {errors.telefon && <p className="mt-1.5 text-sm text-red-500" data-id="element-76">
                    {errors.telefon}
                  </p>}
              </div>

              <div className="sm:col-span-2" data-id="element-77">
                <label htmlFor="poruka" className="mb-2 block text-sm font-medium text-ink" data-id="element-78">
                  Poruka
                </label>
                <textarea id="poruka" rows={5} value={form.poruka} onChange={e => handleChange('poruka', e.target.value)} placeholder="Opišite kakva vam je njega potrebna..." className={`${fieldClass(errors.poruka)} resize-none`} aria-invalid={!!errors.poruka} data-id="element-79" />
                {errors.poruka && <p className="mt-1.5 text-sm text-red-500" data-id="element-80">{errors.poruka}</p>}
              </div>
            </div>

            <button type="submit" disabled={submitting} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-8 py-4 text-sm font-semibold text-white shadow-gold transition-transform hover:scale-[1.01] active:scale-95 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-81">
              {submitting ? <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" data-id="element-82" />
                  Šaljemo...
                </> : <>
                  <SendIcon className="h-4 w-4" data-id="element-83" />
                  Pošaljite poruku
                </>}
            </button>

            {sent && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-4 flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700" data-id="element-84">
                <CheckCircle2Icon className="h-5 w-5" data-id="element-85" />
                Hvala vam! Vaša poruka je uspješno poslana. Javit ćemo se
                uskoro.
              </motion.div>}
          </motion.form>
        </div>
      </div>
    </section>;
}